import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Dayjs } from 'dayjs';
import { Download, Eye, FileSpreadsheet, FileText, FileType, Search } from 'lucide-react';
import { Button, DatePicker, Input, Modal, Segmented, Select, Tag, message } from '../ui';
import type { TagTone } from '../ui';
import { disableFuture } from '../lib/dateRange';
import { api } from '../api/client';
import { previewPdf } from '../lib/pdf';

export type ExportFormat = 'xlsx' | 'pdf' | 'csv';

/**
 * Excel and PDF are what every register offers, and what "export" means in this product.
 *
 * CSV is opt-in per register, for the reader who wants the rows and nothing else — no letterhead, no
 * formatting, no merged summary block — to load into their own model. It is offered only where the
 * server actually serves `<base>.csv`, because a format button that 404s is worse than one absent.
 */
const FORMAT_META: Record<ExportFormat, { label: string; mime: string; icon: ReactNode }> = {
  xlsx: { label: 'Excel', mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', icon: <FileSpreadsheet /> },
  pdf: { label: 'PDF', mime: 'application/pdf', icon: <FileType /> },
  csv: { label: 'CSV', mime: 'text/csv', icon: <FileText /> },
};

/** One labelled block — label above, control below, optional hint under it. */
function XField({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div>
      <div className="mb-1.5 type-label-strong text-content">{label}</div>
      {children}
      {hint && <div className="mt-1 text-xs text-muted">{hint}</div>}
    </div>
  );
}

export interface ExportPickerItem {
  key: string;
  name: string;
  /** Small capsule after the name, e.g. a role or "Cash". */
  tag?: string;
  /** Tone for that capsule — defaults to neutral. Lets a role read as a colour, not just text. */
  tagTone?: TagTone;
  /** A quiet second line under the name (a territory book, a bank branch…). */
  subtitle?: string;
  /**
   * Values this item can be filtered by, keyed by the `filters[].key` below. An item matches a
   * chosen value when its list contains it, so a rep working three districts is found under all
   * three rather than only their "main" one.
   */
  facets?: Record<string, string[] | string | null | undefined>;
}

/**
 * One dropdown above the picker list.
 *
 * `options` and `visibleWhen` take the filters already chosen so a set of them can form a CASCADE
 * (State → District → Beat): each level narrows the next and stays hidden until its parent is
 * picked, instead of offering a flat list of every beat in the country.
 */
/** A choice in one of those dropdowns. A plain string is its own value AND label; the object form
 *  carries a separate label (e.g. a territory id shown as "Pune (9)") and can be disabled. */
export type ExportFilterOption = string | { value: string; label: string; disabled?: boolean };

export interface ExportPickerFilter {
  key: string;
  label: string;
  options: ExportFilterOption[] | ((chosen: Record<string, string>) => ExportFilterOption[]);
  /** Hidden while this returns false — a Beat list means nothing before a District is chosen. */
  visibleWhen?: (chosen: Record<string, string>) => boolean;
  /** The "no filter" option's wording, e.g. "All states". Defaults to `All <label>`. */
  allLabel?: string;
  /** Filters to reset when this one changes — the levels below it in a cascade. */
  clears?: string[];
}

export interface ExportDialogProps {
  open: boolean;
  onClose: () => void;
  /** Dialog heading, e.g. "Export collections". */
  title: string;
  description?: string;
  /** Endpoint WITHOUT its extension — the format is appended (`/payments/export` → `.xlsx`). */
  baseUrl: string;
  /** The page's live filters, carried into the file so it matches the view it came from. */
  baseParams?: Record<string, any>;
  /** Seeds the period; leave undefined for "all time". */
  initialRange?: [Dayjs, Dayjs] | null;
  /** Optional multi-select (staff, bank accounts…). Everything starts selected. */
  picker?: {
    label: string;
    /** Query param that receives the comma-joined keys when it is a real subset. */
    param: string;
    items: ExportPickerItem[];
    loading?: boolean;
    emptyText?: string;
    /** Shown when the list is long enough that scrolling it is not a way to find anybody. */
    searchPlaceholder?: string;
    /** Dropdowns above the list, matched against each item's `facets`. */
    filters?: ExportPickerFilter[];
    /**
     * Keys ticked when the dialog opens, for a page already filtered to somebody. Undefined means
     * everyone, which is the right default for an unfiltered screen — but exporting "everyone" from
     * a page showing one rep is the kind of mismatch nobody notices until the file is already sent.
     */
    initialSelected?: string[];
  };
  /** Optional "how much detail" toggle; sends `detail=0` when set to summary. */
  detailToggle?: { fullLabel: string; summaryLabel: string; fullHint?: string; summaryHint?: string };
  /**
   * Which formats this register serves. Defaults to Excel + PDF — add `'csv'` only where the
   * endpoint really exists. The first entry is what the dialog opens on.
   */
  formats?: ExportFormat[];
  /** A quiet line under the format picker saying what the file will contain. */
  formatHint?: string;
  /**
   * Hide the period picker for registers with no date dimension — a product catalogue, a territory
   * tree, a wallet balance sheet. Offering a date range that changes nothing invites the reader to
   * believe the file was filtered when it was not.
   */
  hidePeriod?: boolean;
  /**
   * What the file will contain, in the reader's words — for a register that exports its OWN live
   * filters instead of asking for them again here.
   *
   * A dialog that has neither a period nor a picker is otherwise a format button with no context:
   * nothing on it says whether the file is the whole log or the eight rows the screen is narrowed
   * to, and the reader finds out only after opening what they downloaded. Pass the period, the
   * people, the filters in force and the sort; a page that asks for its scope in the dialog (its own
   * period picker, its own tick-list) does not need this.
   */
  scopeSummary?: ReactNode;
}

/**
 * The one export dialog for the whole app.
 *
 * A bare "Export" button always produced the same file, so scoping it meant changing the page's filters
 * first — and the downloaded file then gave no clue which filters had produced it. Here the period,
 * format and subset are explicit, they travel to the server as query params, and the server prints them
 * into the file's own header.
 *
 * The saved filename comes from the response's `Content-Disposition`, NOT from anything invented here:
 * the server already names every export `GreenBay_Report_Scope_Period.ext`, and a second name computed
 * on the client would drift from it the moment either side changed.
 */
/**
 * The server's reason, not a shrug.
 *
 * The error body arrives as a Blob because the request asked for one, so it has to be read back out
 * before the message inside it can be shown.
 */
async function readExportError(e: any): Promise<string> {
  const body = e?.response?.data;
  if (body instanceof Blob) {
    try { return JSON.parse(await body.text())?.error?.message ?? EXPORT_FAILED; } catch { return EXPORT_FAILED; }
  }
  return body?.error?.message ?? EXPORT_FAILED;
}

const EXPORT_FAILED = 'Could not export. Try a narrower period or fewer rows.';

export function ExportDialog({
  open, onClose, title, description, baseUrl, baseParams, initialRange, picker, detailToggle, hidePeriod,
  formats, formatHint, scopeSummary,
}: ExportDialogProps) {
  const offered = formats?.length ? formats : (['xlsx', 'pdf'] as ExportFormat[]);
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(initialRange ?? null);
  const [format, setFormat] = useState<ExportFormat>(offered[0]);
  const [detail, setDetail] = useState(true);
  // null = "all", which stays correct when the item list changes underneath.
  const [picked, setPicked] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // A format left selected from a previous open can be one this register does not serve.
    if (open) {
      setRange(initialRange ?? null);
      setPicked(picker?.initialSelected?.length ? picker.initialSelected : null);
      if (!offered.includes(format)) setFormat(offered[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const keys = useMemo(() => (picker?.items ?? []).map((i) => i.key), [picker?.items]);
  const selected = picked ?? keys;
  const toggle = (k: string) =>
    setPicked(selected.includes(k) ? selected.filter((x) => x !== k) : [...selected, k]);

  /* ── Finding people in the list ────────────────────────────────────────────────────────────────
     A picker of 200 reps is a wall of checkboxes: without a search box the only way to export one
     person is to scroll for them, and without territory filters "everyone in Pune" cannot be
     expressed at all. Both narrow only what is SHOWN — the selection itself is never silently
     changed by a filter, so a filter that hides a ticked row does not quietly drop them from the
     file. `Select all` therefore acts on the VISIBLE rows and says so. */
  const [search, setSearch] = useState('');
  const [facets, setFacets] = useState<Record<string, string>>({});
  useEffect(() => { if (open) { setSearch(''); setFacets({}); } }, [open]);

  // Each level resolved against what is already chosen, and dropped entirely while its parent is not.
  const activeFilters = useMemo(
    () => (picker?.filters ?? [])
      .filter((f) => (f.visibleWhen ? f.visibleWhen(facets) : true))
      .map((f) => ({ ...f, opts: typeof f.options === 'function' ? f.options(facets) : f.options })),
    [picker?.filters, facets],
  );
  // A hidden level must not keep filtering the list from off-screen: clearing "State" back to "All"
  // would otherwise leave an invisible District narrowing the results with no way to undo it.
  useEffect(() => {
    const live = new Set(activeFilters.map((f) => f.key));
    const stale = Object.keys(facets).filter((k) => facets[k] && !live.has(k));
    if (stale.length) setFacets((s) => { const n = { ...s }; for (const k of stale) delete n[k]; return n; });
  }, [activeFilters, facets]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (picker?.items ?? []).filter((it) => {
      if (q && !`${it.name} ${it.subtitle ?? ''} ${it.tag ?? ''}`.toLowerCase().includes(q)) return false;
      for (const [k, want] of Object.entries(facets)) {
        if (!want) continue;
        const have = it.facets?.[k];
        const list = Array.isArray(have) ? have : have ? [have] : [];
        if (!list.includes(want)) return false;
      }
      return true;
    });
  }, [picker?.items, search, facets]);

  const visibleKeys = useMemo(() => visible.map((i) => i.key), [visible]);
  const allChecked = visibleKeys.length > 0 && visibleKeys.every((k) => selected.includes(k));
  const narrowed = visible.length !== keys.length;
  const toggleAllVisible = () => setPicked(
    allChecked
      ? selected.filter((k) => !visibleKeys.includes(k))
      : [...new Set([...selected, ...visibleKeys])],
  );

  /** The query the file is built from — shared by preview and download so they cannot diverge. */
  const buildParams = () => {
    const p = new URLSearchParams();
      for (const [k, v] of Object.entries(baseParams ?? {})) {
        if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
      }
      /*
       * ONLY THE VISIBLE PICKER OWNS `from`/`to`.
       *
       * The clear-both branch used to run unconditionally, which quietly deleted a period the PAGE
       * had put in `baseParams` — so a register that filters by date itself and hides this picker
       * (because its own filters already scope the file) exported all time while its table showed
       * one week. With the picker hidden there is nothing here to express a period WITH, so the
       * page's own dates are the only ones there are and they pass straight through.
       */
      if (!hidePeriod) {
        if (range?.[0] && range?.[1]) {
          p.set('from', range[0].format('YYYY-MM-DD'));
          p.set('to', range[1].format('YYYY-MM-DD'));
        } else { p.delete('from'); p.delete('to'); }
      }
      // Only send ids for a real subset — "everything" is the server default and keeps the URL clean.
      // Measured against EVERY item, never against what the search box happens to be showing.
      const allSelected = keys.length > 0 && selected.length === keys.length;
      if (picker && !allSelected) p.set(picker.param, selected.join(','));
      if (detailToggle && !detail) p.set('detail', '0');
    return p;
  };

  /**
   * Look before you keep.
   *
   * A button that drops a file straight into Downloads makes the reader open a desktop app to find
   * out whether the filters were right — and if they were not, the wrong file is already saved and
   * possibly already sent. The PDF is the form every one of these registers can render in the
   * browser, so preview shows THAT, built from the same query as the download. Excel and CSV have no
   * in-browser renderer, so for them this is explicitly labelled "Preview as PDF" rather than
   * pretending to show the spreadsheet.
   */
  const preview = async () => {
    setBusy(true);
    try {
      await previewPdf(() => api.get(`${baseUrl}.pdf?${buildParams().toString()}`, { responseType: 'blob' }));
    } catch (e: any) {
      message.error(await readExportError(e));
    } finally {
      setBusy(false);
    }
  };

  const run = async () => {
    setBusy(true);
    try {
      const p = buildParams();
      const res = await api.get(`${baseUrl}.${format}?${p.toString()}`, { responseType: 'blob' });
      const mime = FORMAT_META[format].mime;
      const disp = String(res.headers?.['content-disposition'] ?? '');
      const served = /filename="([^"]+)"/.exec(disp)?.[1];
      const url = URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement('a');
      a.href = url;
      a.download = served || `export.${format}`;
      document.body.appendChild(a);
      a.click();
      // Revoke on a timer: Chrome needs the URL alive long enough to start the download, and an
      // un-revoked blob leaks for the life of the page.
      setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 100);
      // Say what actually landed in Downloads. "Export downloaded" left the reader unsure whether the
      // filters had applied, how much came out, or which of two files they had just produced — the row
      // count comes from the server (X-Export-Rows), since the client cannot count inside a PDF/xlsx.
      const rows = Number(res.headers?.['x-export-rows']);
      const what = Number.isFinite(rows) ? `${rows.toLocaleString('en-IN')} row${rows === 1 ? '' : 's'}` : null;
      const period = hidePeriod ? null
        : range?.[0] && range?.[1] ? `${range[0].format('DD MMM')} – ${range[1].format('DD MMM YYYY')}`
          : 'all time';
      message.success(
        [`${FORMAT_META[format].label} downloaded`, what, period].filter(Boolean).join(' · ')
        + (served ? ` — ${served}` : ''),
      );
      onClose();
    } catch (e: any) {
      message.error(await readExportError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={(o: boolean) => { if (!o) onClose(); }}
      title={title}
      description={description ?? 'Choose the period, what to include, and the format.'}
      width={620}
      footer={
        <>
          <Button variant="default" onClick={onClose}>Cancel</Button>
          {/* Preview needs the PDF form, so it is only offered where this register serves one. */}
          {offered.includes('pdf') && (
            <Button
              variant={format === 'pdf' ? 'primary' : 'default'}
              loading={busy}
              disabled={!!picker && !selected.length}
              icon={<Eye size={16} />}
              onClick={preview}
            >
              {format === 'pdf' ? 'Preview' : 'Preview as PDF'}
            </Button>
          )}
          <Button
            variant={format === 'pdf' ? 'default' : 'primary'}
            loading={busy}
            disabled={!!picker && !selected.length}
            icon={<Download size={16} />}
            onClick={run}
          >
            Download {FORMAT_META[format].label}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* First, because it is the thing to read before choosing a format: the scope the file
            inherits from the screen. */}
        {scopeSummary && (
          <div className="rounded-card border border-line bg-surface-2/50 px-3.5 py-3">
            <div className="mb-2 type-label-strong text-content">What this file will contain</div>
            {scopeSummary}
          </div>
        )}

        {!hidePeriod && (
          <XField label="Period" hint={range ? undefined : 'Leave empty for all time.'}>
            <DatePicker.RangePicker
              disabledDate={disableFuture}
              value={range as any}
              onChange={(v) => setRange(v as [Dayjs, Dayjs] | null)}
            />
          </XField>
        )}

        <XField
          label="Format"
          hint={[
            formatHint,
            format === 'pdf'
              ? 'Preview opens it in a new tab — download from there if you want to keep it.'
              : 'No browser can render a spreadsheet, so preview shows the PDF of the same rows.',
          ].filter(Boolean).join(' · ')}
        >
          <Segmented
            value={format}
            onChange={(v) => setFormat(v as ExportFormat)}
            options={offered.map((f) => ({ label: FORMAT_META[f].label, value: f, icon: FORMAT_META[f].icon }))}
          />
        </XField>

        {detailToggle && (
          <XField
            label="How much detail"
            hint={detail ? detailToggle.fullHint : detailToggle.summaryHint}
          >
            <Segmented
              value={detail ? 'full' : 'summary'}
              onChange={(v) => setDetail(v === 'full')}
              options={[
                { label: detailToggle.summaryLabel, value: 'summary' },
                { label: detailToggle.fullLabel, value: 'full' },
              ]}
            />
          </XField>
        )}

        {picker && (
          <XField label={`${picker.label} (${selected.length} of ${keys.length} selected)`}>
            {picker.loading ? (
              <div className="rounded-control bg-surface-2 px-3 py-2 text-sm text-muted">Loading…</div>
            ) : !keys.length ? (
              <div className="rounded-control bg-surface-2 px-3 py-2 text-sm text-muted">
                {picker.emptyText ?? 'Nothing to include for these filters.'}
              </div>
            ) : (
              <div className="rounded-control border border-line">
                {/* Search gets its own full-width row: sharing one with three dropdowns squeezed it
                    to about 90px, which truncated its own placeholder. */}
                {(activeFilters.length > 0 || picker.items.length > 8) && (
                  <div className="space-y-2 border-b border-line bg-surface-2/40 px-2.5 py-2">
                    <span className="relative block">
                      <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 z-10 -translate-y-1/2 text-subtle" />
                      <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={picker.searchPlaceholder ?? `Search ${picker.label.toLowerCase()}…`}
                        className="pl-8!"
                        size="small"
                      />
                    </span>
                    {activeFilters.length > 0 && (
                      <div className="flex flex-wrap items-center gap-2">
                        {activeFilters.map((f) => (
                          <Select
                            key={f.key}
                            showSearch
                            optionFilterProp="label"
                            className="min-w-0 flex-1"
                            style={{ minWidth: 130 }}
                            size="small"
                            value={facets[f.key] ?? ''}
                            onChange={(v: any) => setFacets((s) => {
                              const n = { ...s, [f.key]: v || '' };
                              for (const k of f.clears ?? []) delete n[k]; // the levels below this one
                              return n;
                            })}
                            options={[
                              { value: '', label: f.allLabel ?? `All ${f.label.toLowerCase()}` },
                              ...f.opts.map((o) => (typeof o === 'string' ? { value: o, label: o } : o)),
                            ]}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <label className="flex cursor-pointer items-center gap-2 border-b border-line px-3 py-2 type-body-medium">
                  <input
                    type="checkbox"
                    checked={allChecked}
                    disabled={!visible.length}
                    ref={(el) => { if (el) el.indeterminate = !allChecked && visibleKeys.some((k) => selected.includes(k)); }}
                    onChange={toggleAllVisible}
                  />
                  {narrowed ? `Select all ${visible.length} shown` : 'Select all'}
                  {narrowed && <span className="ml-auto type-caption text-subtle">{keys.length - visible.length} hidden by filters</span>}
                </label>
                <div className="max-h-56 overflow-auto">
                  {visible.length === 0 ? (
                    <div className="px-3 py-6 text-center text-sm text-muted">Nothing matches that search.</div>
                  ) : visible.map((it) => (
                    <label key={it.key} className="flex cursor-pointer items-start gap-2 px-3 py-1.5 text-sm hover:bg-surface-2">
                      <input type="checkbox" className="mt-1" checked={selected.includes(it.key)} onChange={() => toggle(it.key)} />
                      <span className="min-w-0 flex-1">
                        <span className="flex flex-wrap items-center gap-1.5">
                          <span className="truncate">{it.name}</span>
                          {it.tag && <Tag tone={it.tagTone ?? 'neutral'} style={{ margin: 0 }}>{it.tag}</Tag>}
                        </span>
                        {it.subtitle && <span className="block truncate type-caption text-subtle">{it.subtitle}</span>}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </XField>
        )}
      </div>
    </Modal>
  );
}
